import React from "react";
import { useOverview } from "@/hooks/useAtlas";
import { useI18n } from "@/i18n";
import { timeAgo } from "@/lib/format";

export function PageHeader({
  title, subtitle, actions, live,
}: { title: string; subtitle?: string; actions?: React.ReactNode; live?: boolean }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-testid="page-header">
      <div>
        <div className="flex items-center gap-2.5">
          {live && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-live/30 bg-live/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-live-400">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-live opacity-70 animate-ping-ring" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-live animate-live-pulse" />
              </span>
              LIVE
            </span>
          )}
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h1>
        </div>
        {subtitle && <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// Top bar for the authenticated shell (mobile/tablet brand + system status).
export function TopBar() {
  const { d, lang } = useI18n();
  const { data } = useOverview();
  const online = !!data;
  return (
    <header
      className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-line bg-navy-950/70 px-4 backdrop-blur-xl lg:h-16 lg:px-8"
      data-testid="app-topbar"
    >
      <div className="flex items-center gap-2 lg:hidden">
        <span className="font-display text-sm font-extrabold tracking-tight text-foreground">SDIP <span className="text-atlas-400">AI</span></span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">ATLAS</span>
      </div>
      <div className="hidden lg:block" />
      <div className="flex items-center gap-3">
        <span className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-medium text-muted-foreground sm:flex" data-testid="system-status">
          <span className={`h-2 w-2 rounded-full ${online ? "bg-atlas animate-live-pulse" : "bg-muted-foreground"}`} />
          {online ? "ATLAS ONLINE" : d.states.reconnecting}
        </span>
        {data?.generated_at && (
          <span className="hidden text-[11px] text-muted-foreground md:inline">
            {d.common.generatedAt}: {timeAgo(data.generated_at, lang)}
          </span>
        )}
      </div>
    </header>
  );
}
